import { useRef, useState } from 'react';

import { useToast } from '@/hooks';
import { thumbnail, uploadFile, imageHandler } from '@/utils';
import IconEdit from '@/assets/icons/ic-edit-white.svg';
import IconRemove from '@/assets/icons/ic-remove-white.svg';
import IconDefault from '@/assets/icons/ic-basic-product.svg';

import { Spinner } from '../Spinner';

import * as S from './styled';

type ImageHandlerType = {
  add: (url: string) => void;
  remove: (sequence: number) => void;
  edit: (url: string, sequence: number) => void;
};

export type MediaType = {
  url: string;
};

interface ImageUploaderProps {
  images: MediaType[];
  handlers: ImageHandlerType;
}

export function ImageUploader(props: ImageUploaderProps) {
  const { images, handlers } = props;

  const toastOn = useToast();
  const [sequence, setSequence] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const registerRef = useRef<HTMLInputElement>(null);
  const modifyRef = useRef<HTMLInputElement>(null);

  const handleSelectFile = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'modify' | 'register',
  ) => {
    if (!(e.target.files && e.target.files.length > 0)) return;
    const reader = new FileReader();

    if (e.target.files![0].size > 10 * 1024 * 1024) {
      toastOn('10MB 이하의 사진만 등록할 수 있습니다.');
      setIsLoading(false);
      return;
    }

    reader.addEventListener('load', async () => {
      setIsLoading(true);
      if (!reader.result) return;

      const fileType = e.target.files![0].type;

      try {
        const imgWidth = await imageHandler.getImageWidth(reader.result.toString(), fileType);
        const image = await imageHandler.resizeImageWithCanvas(
          reader.result.toString(),
          imgWidth,
          fileType,
        );
        const url = await uploadFile(image as Blob);

        if (type === 'register') handlers.add(url);
        else handlers.edit(url, sequence as number);
      } catch (error) {
        toastOn('이미지 등록에 실패했어요 😥');
        console.log(error);
      }

      setIsLoading(false);
      if (registerRef.current) registerRef.current.value = '';
      if (modifyRef.current) modifyRef.current.value = '';
    });

    reader.readAsDataURL(e.target.files[0]);
  };

  return (
    <S.ImageUploaderContainer>
      {images &&
        images.map((v, index) => (
          <div key={index} style={{ position: 'relative' }}>
            {modifyRef.current?.value && isLoading && sequence === index ? (
              <S.LoadingSkeletonBox>
                <Spinner />
              </S.LoadingSkeletonBox>
            ) : (
              <S.ImagePreview src={v ? thumbnail.small(v.url) : ''} />
            )}

            <S.ImageController>
              <S.IconButton
                Icon={IconEdit}
                onClick={() => {
                  setSequence(index);
                  modifyRef.current?.click();
                }}
              />
              <input
                style={{ display: 'none' }}
                ref={modifyRef}
                type="file"
                accept="image/*, .heic"
                onChange={(e) => handleSelectFile(e, 'modify')}
              />
              <S.IconButton Icon={IconRemove} onClick={() => props.handlers.remove(index)} />
            </S.ImageController>
          </div>
        ))}

      {registerRef.current?.value && isLoading && (
        <S.LoadingSkeletonBox>
          <Spinner />
        </S.LoadingSkeletonBox>
      )}
      <input
        style={{ display: 'none' }}
        ref={registerRef}
        type="file"
        accept="image/*, .heic"
        onChange={(e) => handleSelectFile(e, 'register')}
      />

      <S.ImageUploaderBox
        src={IconDefault}
        onClick={() => registerRef.current?.click()}
        width={90}
        height={90}
      />
    </S.ImageUploaderContainer>
  );
}
